import React, { useState } from 'react';
import {
  Form,
  Link,
  redirect,
  useLoaderData,
  useNavigation,
} from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import customFetch from '@utils/customFetch';
import { toast } from 'react-toastify';
import DeleteUserModal from '@components/dashboard/admin/benutzerverwaltung/DeleteUserModal';
import StatusBadge from '@components/dashboard/admin/shared/StatusBadge';
import AddressLines from '@components/shared/AddressLines';
import { USER_STATUS } from '@utils/constants';
import { formatChf, formatDate } from '@utils/format';

// Karten und Bestellungen des Benutzers. Beide Endpoints existieren bereits
// (Admin-Listen mit owner-Filter) — hier wird nur nachgeladen, damit die
// Detailseite zeigt, was dem Benutzer gehört, statt nur sein Profil.
const userCardsQuery = (userId) => ({
  queryKey: ['userCards', userId],
  queryFn: async () => {
    const { data } = await customFetch.get(`/cards?owner=${userId}&limit=100`);
    return data;
  },
});

// Gespeicherte Liefer- und Rechnungsadresse — der Adress-Endpoint lässt den
// Admin per ?userId= auf fremde Konten zu.
const userAddressesQuery = (userId) => ({
  queryKey: ['userAddresses', userId],
  queryFn: async () => {
    const { data } = await customFetch.get(`/address?userId=${userId}`);
    return data;
  },
});

const userOrdersQuery = (userId) => ({
  queryKey: ['userOrders', userId],
  queryFn: async () => {
    const { data } = await customFetch.get(`/orders?owner=${userId}&limit=100`);
    return data;
  },
});

export const loader = async ({ params }) => {
  try {
    const { data } = await customFetch.get(`/users/admin/${params.id}`);
    return data;
  } catch (error) {
    return redirect('/');
  }
};

export const action = async ({ request, params }) => {
  const formData = await request.formData();
  const data = Object.fromEntries(formData);

  try {
    await customFetch.patch(`/users/admin/${params.id}`, data);
    toast.success('Erfolgreich angepasst!');
    return redirect('/dashboard/benutzerverwaltung');
  } catch (error) {
    toast.error(error?.response?.data?.msg);
    return redirect('/dashboard/benutzerverwaltung');
  }
};

const userDetail = () => {
  const user = useLoaderData().user;

  const navigation = useNavigation();
  const isSubmitting = navigation.state === 'submitting';

  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const { data: cardsData, isLoading: cardsLoading } = useQuery(
    userCardsQuery(user._id)
  );
  const { data: ordersData, isLoading: ordersLoading } = useQuery(
    userOrdersQuery(user._id)
  );
  const { data: addressData, isLoading: addressesLoading } = useQuery(
    userAddressesQuery(user._id)
  );
  const cards = cardsData?.cards ?? [];
  const orders = ordersData?.allOrders ?? [];

  return (
    <>
      <DeleteUserModal
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        userId={user._id}
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <nav aria-label="Breadcrumb">
          <ol role="list" className="flex items-center space-x-2">
            <li>
              <div className="flex items-center text-sm">
                <a
                  href="/dashboard/benutzerverwaltung"
                  className="font-medium text-gray-500 hover:text-gray-900"
                >
                  Zurück zu allen Benutzern
                </a>
              </div>
            </li>
          </ol>
        </nav>
        <Link
          to={`/dashboard/protokoll?ref=${user._id}`}
          className="text-sm font-medium text-svisit-primary-600 hover:text-svisit-primary-900"
        >
          Im Audit-Log ansehen
        </Link>
      </div>
      <div className="divide-y divide-gray/5">
        <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Persönliche Informationen
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-700">
              Name, Titel, E-Mail-Adresse, Unternehmen, und URL verwalten.
            </p>
          </div>

          <Form method="PATCH" className="md:col-span-2">
            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:max-w-xl sm:grid-cols-6">
              <div className="col-span-full flex items-center gap-x-8">
                <img
                  src={user.avatarUrl || user.avatar}
                  alt="avatar"
                  className="h-24 w-24 flex-none rounded-lg bg-gray-800 object-cover"
                />

                <div></div>
              </div>

              <div className="col-span-full">
                <label
                  htmlFor="userId"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Benutzer-ID
                </label>
                <div className="mt-2">
                  <input
                    id="userId"
                    name="userId"
                    type="text"
                    readOnly
                    className="block w-full rounded-md border-0 bg-white/5 px-3 py-1.5  shadow-xs ring-1 ring-inset focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6 placeholder:text-gray-400 cursor-auto bg-gray-50 text-gray-500 ring-gray-200"
                    value={user._id}
                  />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Vorname
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="firstName"
                    id="firstName"
                    autoComplete="given-name"
                    className="capitalize block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300  focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={user.firstName}
                  />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Nachname
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="lastName"
                    id="lastName"
                    autoComplete="family-name"
                    className="capitalize block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={user.lastName}
                  />
                </div>
              </div>

              <div className="col-span-full">
                <label
                  htmlFor="title"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Titel / Jobbezeichnung
                </label>
                <div className="mt-2">
                  <input
                    id="title"
                    name="title"
                    type="text"
                    className="block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={user.title}
                  />
                </div>
              </div>

              <div className="col-span-full">
                <label
                  htmlFor="organization"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Unternehmen
                </label>
                <div className="mt-2">
                  <input
                    id="organization"
                    name="organization"
                    type="text"
                    className="block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={
                      user.companyHasSubscription
                        ? user.companyName
                        : user.organization || ''
                    }
                    disabled={!!user.companyHasSubscription}
                  />
                </div>
              </div>

              <div className="col-span-full">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  E-Mail-Adresse
                </label>
                <div className="mt-2">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    className="block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={user.email}
                  />
                </div>
              </div>

              <div className="col-span-full">
                <label
                  htmlFor="url"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  URL des Nutzers
                </label>
                <div className="mt-2">
                  <input
                    id="url"
                    name="url"
                    type="text"
                    className="block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={user.url}
                  />
                </div>
              </div>

              <div className="col-span-full">
                <label
                  htmlFor="status"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Status
                </label>
                <div className="mt-2">
                  <select
                    id="status"
                    name="status"
                    defaultValue={user.status}
                    className="block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                  >
                    {Object.values(USER_STATUS).map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="col-span-full">
                <label
                  htmlFor="isVerified"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Benutzer hat E-Mail bestätigt?
                </label>
                <div className="mt-2">
                  <select
                    id="isVerified"
                    name="isVerified"
                    defaultValue={user.isVerified}
                    className="block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                  >
                    <option>true</option>
                    <option>false</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-8 flex">
              <button
                type="submit"
                className="rounded-md bg-svisit-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-500"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Speichert...' : 'Speichern'}
              </button>
            </div>
          </Form>
        </div>

        <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Adressen
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-700">
              Im Konto gespeicherte Liefer- und Rechnungsadresse. Die Adressen
              einzelner Bestellungen stehen in der Bestellung selbst.
            </p>
          </div>

          <div className="md:col-span-2">
            {addressesLoading ? (
              <p className="text-sm text-gray-500">Lädt …</p>
            ) : (
              <dl className="grid grid-cols-1 gap-6 text-sm sm:grid-cols-2">
                <div>
                  <dt className="font-medium text-gray-900">Lieferadresse</dt>
                  <dd className="mt-3 text-gray-500">
                    {addressData?.shippingAddress ? (
                      <AddressLines address={addressData.shippingAddress} />
                    ) : (
                      <span className="block">Keine Adresse hinterlegt</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-900">
                    Rechnungsadresse
                  </dt>
                  <dd className="mt-3 text-gray-500">
                    {addressData?.billingAddress ? (
                      <AddressLines address={addressData.billingAddress} />
                    ) : (
                      <span className="block">Gleich wie Lieferadresse</span>
                    )}
                  </dd>
                </div>
              </dl>
            )}
          </div>
        </div>

        <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Zugeordnete Karten
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-700">
              Alle Karten, die diesem Benutzer gehören. Der Link führt in die
              Kartenadministration.
            </p>
          </div>

          <div className="md:col-span-2">
            {cardsLoading ? (
              <p className="text-sm text-gray-500">Lädt …</p>
            ) : cards.length === 0 ? (
              <p className="text-sm text-gray-500">
                Diesem Benutzer ist keine Karte zugeordnet.
              </p>
            ) : (
              <ul className="divide-y divide-gray-200 rounded-md ring-1 ring-gray-200">
                {cards.map((card) => (
                  <li
                    key={card._id}
                    className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {card.name || 'Karte'}
                      </p>
                      <p className="truncate text-xs text-gray-500">
                        app.svisit.ch/l/{card.url}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge type="card" status={card.status} />
                      <Link
                        to={`/dashboard/kartenadministration/${card._id}`}
                        className="text-sm font-medium text-svisit-primary-600 hover:text-svisit-primary-900"
                      >
                        Karte verwalten
                        <span className="sr-only">, {card._id}</span>
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Bestellungen
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-700">
              Bestellungen dieses Benutzers, neueste zuerst.
            </p>
          </div>

          <div className="md:col-span-2">
            {ordersLoading ? (
              <p className="text-sm text-gray-500">Lädt …</p>
            ) : orders.length === 0 ? (
              <p className="text-sm text-gray-500">
                Dieser Benutzer hat noch keine Bestellung.
              </p>
            ) : (
              <ul className="divide-y divide-gray-200 rounded-md ring-1 ring-gray-200">
                {orders.map((order) => (
                  <li
                    key={order._id}
                    className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        #{order.orderNumber}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDate(order.createdAt)} ·{' '}
                        {formatChf(order.total)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge type="order" status={order.status} />
                      <Link
                        to={`/dashboard/bestellbearbeitung/${order._id}`}
                        className="text-sm font-medium text-svisit-primary-600 hover:text-svisit-primary-900"
                      >
                        Bestellung öffnen
                        <span className="sr-only">, {order.orderNumber}</span>
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Benutzer löschen
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-700">
              Achtung!!! Diese Option kann nicht rückgänig gemacht werden!
            </p>
          </div>

          <div className="flex items-start md:col-span-2">
            <button
              type="button"
              onClick={() => setOpenDeleteModal(true)}
              className="rounded-md bg-svisit-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-400"
            >
              Benutzer löschen
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default userDetail;
